import { Toast } from "bootstrap";

export type ToastType = "success" | "danger" | "warning" | "info";

export type ToastOptions = {
    type?: ToastType;
    /** Sans titre, le toast se rend sans en-tête : une seule ligne de corps. */
    title?: string;
    /** Par défaut : succès et information s'effacent seuls, avertissement et erreur restent. */
    autohide?: boolean;
    /** Durée d'affichage en millisecondes, quand le toast s'efface seul. */
    delay?: number;
};

const CONTAINER_ID = "toast-container";
// `p-4` et non le `p-3` de l'exemple Bootstrap : 24px, la gouttière de `#admin-content`.
const CONTAINER_CLASS = "toast-container position-fixed bottom-0 end-0 p-4";

// 8 s plutôt que les 5 s de Bootstrap : le temps de lire une phrase entière, et de quoi
// laisser passer une assertion Playwright qui suit une redirection.
const DEFAULT_DELAY = 8000;

// Un succès se lit une fois ; sur une erreur, on veut pouvoir revenir. Les deux types qui
// restent à l'écran sont aussi ceux qu'on annonce en `assertive` aux lecteurs d'écran.
const PERSISTENT: ToastType[] = [ "danger", "warning" ];

// `text-bg-success` et `text-bg-danger` posent un texte blanc, `text-bg-info` et
// `text-bg-warning` un texte noir : la croix suit, sans quoi elle disparaît sur son fond.
const LIGHT_ON_DARK: ToastType[] = [ "success", "danger" ];

const ICONS: Record<ToastType, string> = {
    success: "bi-check-circle-fill",
    danger: "bi-x-octagon-fill",
    warning: "bi-exclamation-triangle-fill",
    info: "bi-info-circle-fill",
};

/**
 * Le conteneur est unique et partagé : `_flash.html.twig` le rend déjà quand la page porte
 * des messages flash, et on le crée à la volée sinon. Deux conteneurs empilés se
 * recouvriraient, le second masquant le premier.
 */
function getContainer(): HTMLElement {
    const existing = document.getElementById(CONTAINER_ID);
    if (existing) {
        return existing;
    }

    const container = document.createElement("div");
    container.id = CONTAINER_ID;
    container.className = CONTAINER_CLASS;
    document.body.append(container);

    return container;
}

function buildCloseButton(type: ToastType, extra: string): HTMLButtonElement {
    const close = document.createElement("button");
    close.type = "button";
    close.className = `btn-close ${LIGHT_ON_DARK.includes(type) ? "btn-close-white" : ""} ${extra}`.replace(/\s+/g, " ").trim();
    close.setAttribute("data-bs-dismiss", "toast");
    close.setAttribute("aria-label", "Fermer");

    return close;
}

function buildIcon(type: ToastType): HTMLElement {
    const icon = document.createElement("i");
    icon.className = `bi ${ICONS[type]} me-2`;

    return icon;
}

function build(message: string, type: ToastType, title?: string): HTMLElement {
    const persistent = PERSISTENT.includes(type);

    const toast = document.createElement("div");
    toast.className = `toast text-bg-${type} border-0${title ? "" : " align-items-center"}`;
    toast.setAttribute("role", persistent ? "alert" : "status");
    toast.setAttribute("aria-live", persistent ? "assertive" : "polite");
    toast.setAttribute("aria-atomic", "true");

    const body = document.createElement("div");
    body.className = "toast-body";

    if (title) {
        const header = document.createElement("div");
        header.className = `toast-header text-bg-${type} border-0`;

        const strong = document.createElement("strong");
        strong.className = "me-auto";
        strong.textContent = title;

        header.append(buildIcon(type), strong, buildCloseButton(type, ""));
        body.textContent = message;
        toast.append(header, body);

        return toast;
    }

    const row = document.createElement("div");
    row.className = "d-flex";

    body.append(buildIcon(type), document.createTextNode(message));
    row.append(body, buildCloseButton(type, "me-2 m-auto"));
    toast.append(row);

    return toast;
}

/**
 * Un toast fermé ne sert plus à rien : on le retire du DOM plutôt que de laisser le
 * conteneur accumuler des noeuds masqués au fil d'une session.
 */
function register(element: HTMLElement): void {
    element.addEventListener("hidden.bs.toast", () => element.remove());
}

/** Affiche un toast et rend son élément, pour laisser l'appelant le fermer avant l'heure. */
export function showToast(message: string, options: ToastOptions = {}): HTMLElement {
    const type = options.type ?? "info";
    const element = build(message, type, options.title);

    getContainer().append(element);
    register(element);

    new Toast(element, {
        autohide: options.autohide ?? !PERSISTENT.includes(type),
        delay: options.delay ?? DEFAULT_DELAY,
    }).show();

    return element;
}

/**
 * Retire tous les toasts affichés, sans attendre leur animation de sortie.
 *
 * Volontairement sans `dispose()` : Bootstrap arme, à chaque `show()` et `hide()`, un
 * `setTimeout` de repli qui rejouera la fin de transition. `dispose()` met `_element` à
 * `null` entre-temps, et ce rappel différé lève alors sur un `classList` introuvable. Sur
 * un noeud simplement détaché, le même rappel s'exécute sans effet visible.
 */
export function clearToasts(): void {
    document.querySelectorAll<HTMLElement>(`#${CONTAINER_ID} .toast`).forEach(element => element.remove());
}

export default function initToasts(): void {
    // Les toasts rendus par Twig arrivent masqués (`.toast:not(.show)` est en `display:none`) :
    // c'est ici qu'ils s'affichent. `getOrCreateInstance` lit au passage les `data-bs-autohide`
    // et `data-bs-delay` posés par le template.
    document.querySelectorAll<HTMLElement>(`#${CONTAINER_ID} .toast`).forEach(element => {
        register(element);
        Toast.getOrCreateInstance(element).show();
    });

    window.qezakoToasts = { show: showToast, clear: clearToasts };
}

declare global {
    interface Window {
        /**
         * Seul global exposé par le bundle : il ouvre les toasts à ce qui ne passe pas par
         * un import — un script inline de template, la console du navigateur, une session de
         * recette qui met un message en scène.
         */
        qezakoToasts: {
            show: typeof showToast;
            clear: typeof clearToasts;
        };
    }
}
